import { useCMSPageField } from '@/cms/fields'
import { Custom500 } from '@/pages/500'

import { Basket } from '@/scopes/checkout/components/Basket'

import { CheckoutError } from '@/scopes/checkout/components/CheckoutError'

import { CheckoutHeading, CheckoutHeadingTitle } from '@/scopes/checkout/components/CheckoutHeading'
import { CheckoutNextStep } from '@/scopes/checkout/components/CheckoutNextStep'
import { CheckoutPreviousStep } from '@/scopes/checkout/components/CheckoutPreviousStep'
import { CheckoutProgress } from '@/scopes/checkout/components/CheckoutProgress'
import { GenerateSIDButton } from '@/scopes/checkout/components/GenerateSIDButton'
import { Content, Loader, MainContent, PageLayout, SideBarContent, Wrapper } from '@/scopes/checkout/components/Layout'
import { useSendCheckoutEvent } from '@/scopes/checkout/domain/analytics/useSendCheckoutEvent'
import { CheckoutContextProvider } from '@/scopes/checkout/domain/CheckoutContext'
import { useBookingDraftQuery, useCheckoutSid } from '@/scopes/checkout/domain/hooks'
import { useUpdateLoginButtonLinks } from '@/scopes/checkout/pages/helpers/RedirectLinks'
import { DevTools } from '@/scopes/common/components/DevTools/DevTools'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import { SpaceBetween, tk } from '@sunwebgroup/ui-components'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { Header } from '../../common/components/Header'
import { CheckoutBenefits } from '../components/CheckoutBenefits'
import { CheckoutMessage } from '../components/CheckoutMessage'
import { CouponCodeForm } from '../components/CouponCode/CouponCodeForm'
import { EngineError } from '../components/EngineError/EngineError'
import { CheckoutPriceOverview } from '../components/PriceOverview/CheckoutPriceOverview'

const queryClient = new QueryClient()

function Page() {
  const { isLoading, data } = useBookingDraftQuery()
  const intl = useIntl()

  useSendCheckoutEvent()
  useUpdateLoginButtonLinks()

  const priceOverivewComponents = [
    '40411206-1542-430d-9019-dc30b77fb602' /* ACPT NL SUMMER PCO DEFAULT */,
    '3073a9b9-7a36-4d53-960b-639781c1f1e1' /* ACPT NL SUMMER PCO VARIANT */,
    '6045a317-291c-40a3-ab81-1f8ce5108384' /* ACPT NL WINTER PCO DEFAULT */,
    '966e189a-4a8e-433a-aece-7a1f516fa2c9' /* ACPT NL WINTER PCO VARIANT */,
    /* TODO: DO THE PROD ONES */
  ]

  const priceOverivewPerPersonComponents = [
    '8b2d4af9-288f-423a-a52f-2af7c110c0a0' /* ACPT NL SUMMER PPPCO DEFAULT */,
    '089c323c-5fc2-4e1a-aef2-1ee9e2481ccf' /* ACPT NL SUMMER PPPCO VARIANT */,
    'f5a0e8a2-ce92-449b-8fbf-8bcc26d8a390' /* ACPT NL WINTER PPPCO DEFAULT */,
    'c9773e72-cdac-4c91-b1eb-6e66ab228a34' /* ACPT NL WINTER PPPCO VARIANT */,
    /* TODO: DO THE PROD ONES */
  ]

  const messageComponents = [
    '305d0b54-829a-4c60-9155-99add4ac80a1' /* ACPT NL SUMMER DEFAULT */,
    '28aff714-7169-4223-b6b7-46b630edf582' /* ACPT NL SUMMER DEFAULT */,
    '12578b75-13fe-48fd-bc2b-adc01985a391' /* ACPT NL SUMMER VARIANT */,
    '3fd2930e-ec49-47ac-9456-141dba9128ea' /* ACPT NL SUMMER VARIANT */,

    '74094dbf-75bf-44c8-b036-39aa940b604a' /* ACPT NL WINTER DEFAULT */,
    '94f92def-a5af-4d05-9cc5-0c0b5e872a7a' /* ACPT NL WINTER VARIANT */,
  ]

  return (
    <PageLayout>
      {data?.error && data.error.hasError && !data.error.errorCode && <CheckoutError />}
      {data?.error && data.error.hasError && data.error.errorCode && (
        <>
          <Header hideLiveSearch hideNavItems />
          <NewRelicErrorBoundary scope="checkout-page" fallback={<Custom500 brand="sunweb" />}>
            <EngineError />
          </NewRelicErrorBoundary>
        </>
      )}
      {(!data?.error || (data?.error && !data.error.hasError)) && (
        <>
          <Header hideLiveSearch hideNavItems />
          <NewRelicErrorBoundary scope="checkout-page" fallback={<Custom500 brand="sunweb" />}>
            <Wrapper>
              {isLoading
                ? (
                    <Loader />
                  )
                : (
                    <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
                      <CheckoutPreviousStep />
                      <CheckoutProgress />
                      <Content>
                        <MainContent>
                          <CheckoutHeading direction="y">
                            <CheckoutHeadingTitle>
                              {intl.formatMessage({ id: 'checkout_steps_header_step_2_title' })}
                            </CheckoutHeadingTitle>
                          </CheckoutHeading>
                          <CheckoutPriceOverview
                            componentsId={priceOverivewComponents}
                            title={intl.formatMessage({ id: 'checkout_price_overview' })}
                            glyph="money"
                          />
                          {messageComponents.map(componentId => (
                            <CheckoutMessage key={componentId} componentId={componentId} />
                          ))}
                          <CheckoutPriceOverview
                            componentsId={priceOverivewPerPersonComponents}
                            title={intl.formatMessage({ id: 'checkout_price_overview_per_person' })}
                            glyph="participantsBadge"
                          />
                          <CouponCodeForm />
                          <CheckoutNextStep />
                        </MainContent>
                        <SideBarContent direction="y" spacing={tk('spacing_lg')}>
                          <Basket />
                          <CheckoutBenefits />
                        </SideBarContent>
                      </Content>
                    </SpaceBetween>
                  )}
            </Wrapper>
          </NewRelicErrorBoundary>
        </>
      )}
    </PageLayout>
  )
}

export default function CheckoutPageStep2() {
  const sid = useCheckoutSid()
  const contextItemId = useCMSPageField('contextItemId')

  return (
    <QueryClientProvider client={queryClient}>
      <CheckoutContextProvider
        value={{
          contextItemId,
        }}
      >
        <PageLayout>{sid ? <Page /> : <GenerateSIDButton />}</PageLayout>
        <DevTools />
      </CheckoutContextProvider>
    </QueryClientProvider>
  )
}
